import {gsap} from "gsap/all";
import $ from "jquery";
import {BaseGameMars} from "../base/BaseGameMars";
import {Game} from "./Game";
import {BackgroundMorpher} from "../path-to-mars/BackgroundMorpher";
import {BuildYourRoverScreenTransition} from "./BuildYourRoverScreenTransition";

// Constructor
export class BuildYourRover extends BaseGameMars {

  constructor(side) {
    super(`data/build-your-rover.json`, 'build-your-rover');
    gsap.registerPlugin();

    // Which station this is (left or right), defaults to left
    this.side = (side === "right") ? "right" : "left";
    $("body").addClass(`side-${this.side}`);
  }

  /**
   * Overrides parent's function to load data from the specified file and ensures that all
   * rover entries share a common template by copying properties from the first
   * rover object to the rest.
   */
  async loadData(filename = null) {
    await super.loadData(filename);
    if (filename === null) {
      let template = null
      for (let roverId of Object.keys(this.data.content.game.rover)) {
        if (template === null) {
          // Use first rover as template
          template = this.data.content.game.rover[roverId];
          continue;
        }
        // Extend data for the following rovers
        this.data.content.game.rover[roverId] = $.extend(true, {}, template, this.data.content.game.rover[roverId])
        console.log("override data for rover", roverId, this.data.content.game.rover[roverId]);
      }
    }
  }

  initGame() {
    // Setup side
    if (this.side === "right") $("[data-side='left']").remove();
    else $("[data-side='right']").remove();

    // Always-visible background that morphs shape per screen. 0.4s duration
    // matches the base screen fade-out so the morph completes before fade-in.
    this.backgroundMorpher = new BackgroundMorpher(this, {duration: 2});
    // Set the first screen's shape instantly so it doesn't morph in on load.
    this.backgroundMorpher.to("rect", {instant: true});

    // Drives screen transitions; morphs the background as part of each one.
    this.screenTransition = new BuildYourRoverScreenTransition(this.backgroundMorpher);

    // Audio example
    // setInterval(() => app.audio.play("audio/click.wav", 1, true), 1000)

    this.game = new Game(this);
    this.initMQTTScreenCommunication("rover");
    super.initGame();

    // TODO: remove debug lines (Rover config test)
    // super.initGame(3);
    // setTimeout(() => {
    //   this.game.rovers[0].applyConfig("A206060606")
    //   this.game.updateQRCode();
    //   this.publishScreenBridgeMessage("rover", this.game.getConfigCode());
    // }, 2000)

    // TODO: remove debug lines (go quickly to the game screen)
    // this.backgroundMorpher.duration = 0.1
    // setTimeout(() => {
    //   this.goToScreen(3, () => {
    //     this.beforeStep(2);
    //     this.currentTimeline.progress(1).kill();
    //     this.beforeStep(3);
    //   }, true);
    // }, 451)
  }

  // Curtain transition. The screens are clip-masked to the background shape, so
  // collapsing the shape hides the content. Sequence: close the curtain, then
  // (only once fully closed) swap the screen *instantly* — no content fade — then
  // reopen the curtain to the new screen's shape. We claim the lock/target
  // guards here and tell super to skip them, since it runs inside the close
  // callback rather than up front.
  goToScreen(screenId, callback = null, skipLockScreenCheck = false, options = {}) {
    // Switch screen without animation/delay
    const isInstant = options.instant || false;

    // Initial reveal (boot, no screen shown yet): no curtain — the shape is
    // already set instantly in initGame, just let the base reveal screen 1.
    if (this.currentScreen === 0) {
      return super.goToScreen(screenId, callback, skipLockScreenCheck, options);
    }

    // Same guards the base applies, evaluated up front (before we start closing).
    if (!skipLockScreenCheck && this.lockScreen) return false;
    if (this.targetScreen === screenId) return;

    this.targetScreen = screenId;
    this.lockInteraction();

    const shape = this.screenTransition.shapeForScreen(screenId);
    const currentShape = this.backgroundMorpher.current;

    // Check if the current shape and the new target shape are the same
    const isSameShape = currentShape === shape;

    const fullDuration = isInstant ? 0 : this.backgroundMorpher.duration;
    const halfDuration = fullDuration / 2;
    const hold = isInstant ? 0 : this.backgroundMorpher.closedHold;
    const $leaving = this.$currentScreen;

    // When swap is complete, and the animation inside
    const onTransitionComplete = () => {
      this.unlockInteraction();
      if (typeof callback === "function") callback();
    };

    // Swap the screen with no fade (skipOut/skipIn), bypassing the guards we
    // already claimed above.
    const swapScreen = (afterSwap) => {
      super.goToScreen(screenId, afterSwap, true, {skipOut: true, skipIn: true, allowSameScreenTransition: true});
    };

    // Fade the entering screen in from alpha 0 using the given gsap vars.
    const fadeInEntering = (vars) => {
      const $entering = this.$currentScreen;
      gsap.set($entering, {alpha: 0});
      gsap.to($entering, {alpha: 1, duration: vars.duration, delay: vars.delay, ease: vars.ease});

      // Target elements inside the current screen with your specific class (e.g. ".animate-fade-up")
      const $staggerElements = $entering.find(".animate-fade-up");
      if ($staggerElements.length && !isInstant) {
        gsap.set($staggerElements, {alpha: 0, y: 20});
        gsap.to($staggerElements, {
          alpha: 1,
          y: 0,
          duration: vars.duration,
          delay: vars.delay || 0,
          stagger: 0.125,
          ease: vars.ease,
          onComplete: vars.onComplete
        });
      } else {
        if ($staggerElements.length) {
          gsap.set($staggerElements, {alpha: 1, y: 0});
        }
        if (typeof vars.onComplete === "function") vars.onComplete();
      }
    };

    // Fade the leaving screen out, if there is one, using the given duration/ease.
    const fadeOutLeaving = (duration, ease) => {
      if ($leaving && $leaving.length) {
        if (isInstant) {
          gsap.set($leaving, {alpha: 0});
        } else {
          gsap.to($leaving, {alpha: 0, duration, ease});
        }
      }
    };

    // If shapes identical, don't animate the background
    if (isSameShape || isInstant) {
      fadeOutLeaving(isInstant ? 0 : halfDuration * 0.5, this.backgroundMorpher.shapeEase);

      if (isInstant) {
        swapScreen(() => {
          fadeInEntering({
            duration: 0,
            onComplete: onTransitionComplete
          });
        });
      } else {
        gsap.delayedCall(halfDuration * 0.5, () => {
          swapScreen(() => {
            fadeInEntering({
              duration: halfDuration * 0.5,
              ease: this.backgroundMorpher.shapeEase,
              onComplete: onTransitionComplete
            });
          });
        });
      }

      return true;
    }

    // 1) Close the curtain, fading the leaving content out in lockstep so
    //    nothing shows through the thin closed slit.
    fadeOutLeaving(halfDuration, this.backgroundMorpher.closeEase);

    this.backgroundMorpher.close(() => {
      // 2) Curtain is shut — swap the screen with no fade (skipOut/skipIn) and
      //    bypass the guards we already claimed above.
      const afterClosed = () => {
        swapScreen(() => {
          // Base unlocks at the end of its (instant) swap; keep it locked until the
          // new content (and curtain, if reopening) finish coming in.
          this.lockInteraction();

          if (shape === "closedFade") {
            // Background stays faded out — just fade the new content back in.
            fadeInEntering({duration: halfDuration, ease: this.backgroundMorpher.openEase, onComplete: onTransitionComplete});
          } else {
            // Keep the new content hidden through the closed + hold phase, then
            // fade it in as the curtain reopens (so it's not visible in the slit).
            fadeInEntering({duration: halfDuration, delay: hold, ease: this.backgroundMorpher.openEase});
            // 3) Reopen the curtain to the new screen's shape.
            this.backgroundMorpher.open(shape, onTransitionComplete);
          }
        });
      };

      // If closedFade, fade the background
      if (shape === "closedFade") {
        this.backgroundMorpher.fadeOut(afterClosed);
      } else {
        afterClosed();
      }
    });

    return true;
  }

  initScreen(screenId) {
    const targetScreenNoSuffix = parseInt(screenId.toString().split("-")[0]);
    this.currentTimeline = gsap.timeline();

    // Glow under the "went to Mars" box swells in each time screen 4 shows
    $("#screen-4 .left-text").toggleClass("is-glowing", targetScreenNoSuffix === 4);

    if (targetScreenNoSuffix === 3) {
      // When coming from screen 4, partially reset
      if (this.currentScreen && this.currentScreen === 4) {
        this.game.reset(true); // partial reset
      } else {
        // When applying config using code, skip to the end of step tutorial
        if (this.game.appliedConfig) {
          this.beforeStep(3);
        } else {
          this.beforeStep(1);
        }
      }
    } else if (targetScreenNoSuffix === 4) {
      console.log(`Publish ${this.game.getConfigCode()} to MQTT.`)
      this.publishScreenBridgeMessage("rover", this.game.getConfigCode());
    }
  }

  beforeStep(stepId) {
    this.$currentScreen.addClass("step-active");
    this.timeline = gsap.timeline();
    gsap.set($(".step.current"), {autoAlpha: 0, y: 20});
    gsap.set($(".middle-panel"), {opacity: 0.1, pointerEvents: "none"});

    this.initStep(stepId);
  }

  initStep(stepId) {
    this.currentTimeline = gsap.timeline();

    if (stepId === 1) {
      this.currentTimeline
        .fromTo($(".right-panel .rover-choice-btn, .right-panel .finishVehicle"), {
          scale: 0.95, opacity: 0.1,
        }, {
          scale: 1, opacity: 1,
          duration: 0.5, ease: "power1.out"
        }).fromTo($(".step.current"), {
        autoAlpha: 0, y: 20,
      }, {
        y: 0, autoAlpha: 1,
        duration: 0.3, ease: "power1.out"
      }, "<");
    } else if (stepId === 2) {
      this.currentTimeline
        .to($(".right-panel .rover-choice-btn .pill-button .glow"),
          {opacity: 0, duration: 0.3, ease: "power1.out"})
        .to($(".right-panel .rover-choice-btn, .right-panel .finishVehicle"),
          {opacity: 0.1, duration: 0.3, ease: "power1.out"}, "<")
        .to($(".left-panel .property-list"),
          {opacity: 1, scale: 1, duration: 0.3, ease: "power1.out"}, "<")
        .to($(".left-panel .others-buttons"),
          {opacity: 1, scale: 1, duration: 0.3, ease: "power1.out"}, '<')
        .to($(".left-panel .others-buttons .property-description"),
          {boxShadow: "0px 0px 36.5px 0px rgba(255,255,255,1)", duration: 0.3, ease: "power1.out"}, '<')
        .to($(".left-panel .border-shape"),
          {opacity: 1, duration: 0.3, ease: "power1.out"}, '<')
        .to($(".left-panel .pill-button .glow"),
          {opacity: 1, duration: 0.3, ease: "power1.out"}, '<')
    } else if (stepId === 3) {
      this.currentTimeline
        .set($(".right-panel .rover-choice-btn > div:not([data-action-arg='cockpit'])"),
          {opacity: 0.5})
        .call(() => {
          $(".right-panel .rover-choice-btn > div:not([data-action-arg='cockpit'])").addClass("not-active");
        }, null, '<')
        .to($(".left-panel .property-list"),
          {opacity: 1, duration: 0.3, ease: "power1.out"}, '<')
        .to($(".left-panel .others-buttons .property-description"),
          {boxShadow: "0px 0px 36.5px 0px rgba(255,255,255,0)", duration: 0.3, ease: "power1.out"}, '<')
        .to($(".left-panel .border-shape"),
          {opacity: 0, duration: 0.3, ease: "power1.out"}, '<')
        .to($(".right-panel .rover-choice-btn"),
          {opacity: 1, duration: 0.3, ease: "power1.out"}, '<')
        .to($(".right-panel .rover-choice-btn > div[data-action-arg='cockpit']"),
          {opacity: 1, pointerEvents: "auto", duration: 0.3, ease: "power1.out"}, '<')
        .to($(".middle-panel"),
          {autoAlpha: 1, pointerEvents: "auto", duration: 0.3, ease: "power1.out"}, '<')
        .call(() => {
          this.$currentScreen.removeClass("step-active");
        }, null);
    }
  }

  /**
   * Reset function, used to go back clean version of the game
   * @param screenId
   */
  reset(screenId) {
    // Reset variable and timeline
    this.killTimeline(this.currentTimeline);
    this.killTimeline(this.timeline);

    const resetCallback = () => {
      // TODO Things that reset AFTER goToScreen
      this.game.reset();

      // Reset steps elements
      const elementToClear = [
        ".right-panel .rover-choice-btn",
        ".middle-panel",
        ".right-panel .rover-choice-btn .pill-button .glow",
        ".left-panel .property-list",
        ".left-panel .others-buttons",
        ".left-panel .others-buttons .property-description",
        ".left-panel .border-shape",
        ".left-panel .pill-button .glow",
        ".step",
      ].join(",");
      gsap.set($(elementToClear), {clearProps: "all"});
      // Finish button is the pill button itself, clearing "all" would wipe its size variables (--pill-h, ...) from the template
      gsap.set($(".right-panel .finishVehicle"), {clearProps: "opacity,transform"});
      gsap.set(".right-panel .rover-choice-btn .pill-button", {clearProps: "pointerEvents"});
      this.$currentScreen.removeClass("step-active");
    }

    // Super
    return super.reset(screenId, resetCallback);
  }

}
